/**
 * The password prompt, and the control that made two verbs reachable for the first time.
 *
 * `requireElevation()` guards exactly two routes: `POST /family/withdraw` and `POST /market/list`.
 * Both move something a stolen cookie should not be able to move on its own, which is why they are
 * guarded, and `POST /api/auth/elevate` has answered correctly since Phase 1. What did not exist
 * was anything that asked the player for the password, so both routes refused every player who
 * ever pressed them and no screen could clear it. Emptying a family treasury and listing on the
 * market were unreachable for the entire life of this game.
 *
 * It lives in the SHELL rather than on either screen because the refusal can come from either one,
 * and because the answer is identical wherever it came from.
 *
 * THE GOOGLE CASE IS NAMED RATHER THAN LEFT TO FAIL. An account created with Sign in with Google,
 * or one that later linked it, carries a deliberately UNUSABLE password: `google.ts` writes the
 * hash of a random string it then throws away, which is what closes the account pre-hijacking
 * hole. Such a player has no password to re-enter and would sit at this prompt failing forever.
 * The panel says so and points at the reset flow, which is how they set a real one.
 */

import { useEffect, useRef, useState } from 'react'
import { Icon } from '../ui/Icon'
import { Btn, Panel, PanelHead, Row } from '../ui/Kit'
import { useSession } from '../game/session'
import { sz } from '../ui/viewport'

export function Elevate() {
  const open = useSession((s) => s.needsElevation)
  const error = useSession((s) => s.elevationError)
  const busy = useSession((s) => s.busy)
  const elevate = useSession((s) => s.elevate)
  const cancel = useSession((s) => s.cancelElevation)
  const [password, setPassword] = useState('')
  const field = useRef<HTMLInputElement>(null)

  // The password is never kept after the prompt closes, landed or refused.
  useEffect(() => {
    if (!open) setPassword('')
    else field.current?.focus()
  }, [open])

  // Escape walks away. A modal that can only be dismissed by succeeding is a trap.
  useEffect(() => {
    if (!open) return
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') cancel()
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [open, cancel])

  if (!open) return null

  const submit = () => {
    if (password === '' || busy) return
    void elevate(password)
  }

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label="Confirm your password"
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 80,
        display: 'grid',
        placeItems: 'center',
        background: 'rgba(3, 5, 7, 0.72)',
        backdropFilter: 'blur(3px)',
        padding: 20,
      }}
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) cancel()
      }}
    >
      <div style={{ width: 'min(430px, 100%)' }}>
        <Panel tone="danger">
          <PanelHead title="Say it again" icon="lock" accent="var(--gold)" />
          <div style={{ padding: '14px 16px 16px' }}>
            <p style={{ margin: '0 0 14px', fontSize: 12.5, color: 'var(--ink-mid)', lineHeight: 1.55 }}>
              Moving money out of a treasury and putting goods on the market both need the password
              again, even though you are already signed in. It is the one thing somebody who stole
              the cookie on this machine would not have.
            </p>

            <label
              className="t-label"
              style={{ display: 'block', fontSize: sz(9.5), color: 'var(--ink-dim)', marginBottom: 5 }}
              htmlFor="elevate-password"
            >
              Password
            </label>
            <input
              id="elevate-password"
              ref={field}
              type="password"
              autoComplete="current-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') submit()
              }}
              style={{
                width: '100%',
                padding: '9px 11px',
                background: 'var(--char)',
                border: '1px solid var(--line)',
                borderRadius: 2,
                color: 'var(--ink-hi)',
                font: 'inherit',
                fontSize: 13,
              }}
            />

            {error !== null && (
              <Row gap={7} style={{ marginTop: 11, alignItems: 'flex-start' }}>
                <Icon n="warn" s={13} c="var(--arrest)" />
                <span style={{ fontSize: 12, color: 'var(--arrest)', lineHeight: 1.5 }}>{error}</span>
              </Row>
            )}

            <div style={{ display: 'flex', gap: 8, marginTop: 16 }}>
              <Btn v="gold" full onClick={submit} disabled={busy || password === ''}>
                {busy ? 'CHECKING' : 'GO ON'}
              </Btn>
              <Btn full onClick={cancel} disabled={busy}>
                NOT NOW
              </Btn>
            </div>

            {/*
              The Google-only account, which has no password to give. Always shown rather than
              detected: the client is not told whether the account has a usable password, and
              guessing wrong here means telling somebody their correct password is wrong.
            */}
            <p style={{ margin: '14px 0 0', fontSize: 11, color: 'var(--ink-dim)', lineHeight: 1.5 }}>
              Signed in with Google and never set a password? You do not have one to give. Use
              &ldquo;Forgotten your password&rdquo; on the sign-in screen once to set one, and it
              will work here from then on.
            </p>
          </div>
        </Panel>
      </div>
    </div>
  )
}
